import { useEffect, useMemo, useState } from 'react'

/**
 * Keeps the screen awake while Namu is in a conversation.
 *
 * Someone talking to their phone is not touching it, so the auto-lock timer runs
 * out mid-sentence and the session dies for no reason the person can see. A
 * screen wake lock is the fix, with two behaviours that are easy to get wrong:
 *
 * The browser releases the lock whenever the page is hidden. A lock taken once
 * at the start of a session is therefore gone the moment someone checks a
 * message and comes back, so it has to be re-acquired on every return to
 * visibility.
 *
 * And `request` rejects for ordinary reasons: low battery, power saving, a
 * hidden document. Those are refusals, not failures. Every call is wrapped, and
 * the worst outcome of a rejection is that the screen dims the way it always did.
 */

interface WakeLockSentinelLike {
  release(): Promise<void>
  addEventListener?(type: 'release', listener: () => void): void
  removeEventListener?(type: 'release', listener: () => void): void
}

interface WakeLockLike {
  request(type: 'screen'): Promise<WakeLockSentinelLike>
}

function getWakeLock(): WakeLockLike | null {
  if (typeof navigator === 'undefined') return null
  const candidate = (navigator as Navigator & { wakeLock?: WakeLockLike }).wakeLock
  if (!candidate || typeof candidate.request !== 'function') return null
  return candidate
}

function isVisible(): boolean {
  if (typeof document === 'undefined') return false
  return document.visibilityState === 'visible'
}

export function useWakeLock(active: boolean): { supported: boolean; held: boolean } {
  const supported = getWakeLock() !== null
  const [held, setHeld] = useState(false)

  useEffect(() => {
    if (!supported || !active) return

    let cancelled = false
    let sentinel: WakeLockSentinelLike | null = null

    const handleReleased = (): void => {
      // Fired when the system drops the lock on its own, typically because the
      // screen went off. Reporting held when it is not would be a lie the UI
      // would happily repeat.
      sentinel = null
      if (!cancelled) setHeld(false)
    }

    const acquire = async (): Promise<void> => {
      const wakeLock = getWakeLock()
      if (!wakeLock || cancelled || sentinel) return
      // A hidden document always rejects, so asking is pure noise in the console.
      if (!isVisible()) return

      try {
        const next = await wakeLock.request('screen')
        if (cancelled) {
          try {
            await next.release()
          } catch {
            // Already released by the browser, which is the outcome we wanted.
          }
          return
        }
        sentinel = next
        next.addEventListener?.('release', handleReleased)
        setHeld(true)
      } catch {
        if (!cancelled) setHeld(false)
      }
    }

    const handleVisibilityChange = (): void => {
      if (!isVisible()) {
        // The browser has already taken it back. Drop the reference so the next
        // acquire is not short-circuited by a sentinel that is no longer live.
        sentinel = null
        setHeld(false)
        return
      }
      void acquire()
    }

    void acquire()
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      const current = sentinel
      sentinel = null
      setHeld(false)
      if (!current) return
      current.removeEventListener?.('release', handleReleased)
      try {
        void current.release().catch(() => {})
      } catch {
        // Releasing an already-released sentinel throws in some builds.
      }
    }
  }, [active, supported])

  // Stable while the values are, so a caller can put this in a dependency array.
  return useMemo(() => ({ supported, held }), [supported, held])
}
